import { Alert, Button, Divider, Flex, Group, Modal, Stack, Text, Title } from '@mantine/core'
import { PageContainer } from '@shared/components/app-shell/page-container/page-container'
import { PAGE_TITLES } from '@/constants/page-titles'
import {
  useAuthenticatedGraphQuery,
  useAuthenticatedGraphMutation,
} from '@/hooks/use-authenticated-query'
import { GetWelcomeMessageGql } from '@/graphql/queries/admin/get-welcome-message.graphql'
import { AddWelcomeMessageGql } from '@/graphql/mutations/add-welcome-message'
import { MutationResponse } from '@shared/loom-api/.generated/types'
import {
  showErrorNotification,
  showSuccessNotification,
} from '@shared/helpers/notifications/notifications'
import { useEffect, useState } from 'react'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Markdown } from 'tiptap-markdown'

import { RichTextEditor, Link } from '@mantine/tiptap'

import Underline from '@tiptap/extension-underline'
import { IconCancel, IconInfoCircle } from '@tabler/icons-react'
import { useDisclosure } from '@mantine/hooks'

export function AdminPage() {
  const [submitModalState, { open: openSubmitModal, close: closeSubmitModal }] =
    useDisclosure(false)

  // Get the unsold lots data
  const { data } = useAuthenticatedGraphQuery({
    queryKey: ['welcomeMessageGql'],
    gql: GetWelcomeMessageGql,
  })

  const [welcomeMessage, setWelcomeMessage] = useState<string | null>('')

  useEffect(() => {
    setWelcomeMessage(data?.welcomeMessage ?? '')
  }, [data?.welcomeMessage])

  const addWelcomeMessageMutation = useAuthenticatedGraphMutation({
    gql: AddWelcomeMessageGql,
    gqlKey: 'addWelcomeMessage',
    onSuccess: (mutation) => {
      const responseData: MutationResponse = mutation.addWelcomeMessage
      if (responseData.success) {
        showSuccessNotification('Updated')
      } else {
        showErrorNotification(`An error occurred: ${responseData.errorMessage}`)
      }
    },
  })

  const editor = useEditor(
    {
      extensions: [
        StarterKit,
        Link,
        Underline,

        Markdown.configure({
          html: true, // Allow HTML in markdown
          tightLists: true,
          bulletListMarker: '-',
          linkify: false,
          breaks: false,
        }),
      ],
      content: welcomeMessage,
    },
    [welcomeMessage]
  )

  const save = async () => {
    addWelcomeMessageMutation.mutate({ message: editor?.storage.markdown.getMarkdown() })
  }

  const clear = async () => {
    closeSubmitModal()
    setWelcomeMessage('')
    addWelcomeMessageMutation.mutate({ message: '' })
  }

  return (
    <PageContainer title={PAGE_TITLES.Admin}>
      <Stack>
        <Title>Admin</Title>
        <Divider my='sm' />
        <Title order={3}>Edit Grower Welcome Message</Title>

        <Alert icon={<IconInfoCircle />} title='Note' color='blue'>
          Once a message is entered and updated, users will be able to see it on the Grower Gateway.
          Please check your spelling and grammar.
        </Alert>
        <Group>
          <RichTextEditor aria-label='rich-text-editor' editor={editor} style={{ width: '100%' }}>
            <RichTextEditor.Toolbar sticky stickyOffset='var(--docs-header-height)'>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
                <RichTextEditor.ClearFormatting />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.H1 />
                <RichTextEditor.H2 />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Hr />
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Link />
                <RichTextEditor.Unlink />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Undo />
                <RichTextEditor.Redo />
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>

            <RichTextEditor.Content data-testid='rte-content' />
          </RichTextEditor>
        </Group>
        <Group justify='flex-end'>
          <Button
            size='md'
            type='submit'
            color='red'
            variant='outline'
            onClick={() => openSubmitModal()}
            loading={addWelcomeMessageMutation.isPending}
            loaderProps={{ type: 'dots' }}
            leftSection={<IconCancel />}
          >
            Clear
          </Button>
          <Button
            size='md'
            type='submit'
            onClick={() => save()}
            loading={addWelcomeMessageMutation.isPending}
            loaderProps={{ type: 'dots' }}
          >
            Update
          </Button>
        </Group>
        <Modal
          data-testid='send-offer-modal'
          title={
            <Flex justify='flex-start' align='center'>
              <Text fw={500} size='lg'>
                Clear Confirmation
              </Text>
            </Flex>
          }
          opened={submitModalState}
          onClose={closeSubmitModal}
          centered
          closeButtonProps={{ 'aria-label': 'Close modal' }}
          returnFocus={false}
        >
          Are you sure you want to clear the welcome message?
          <Group justify='flex-end' mt='md' gap='xs'>
            <Button variant='transparent' onClick={closeSubmitModal}>
              Cancel
            </Button>
            <Button
              aria-label='confirm-clear-welcome-message'
              leftSection={<IconCancel />}
              color='red'
              variant='outline'
              onClick={() => clear()}
            >
              Clear
            </Button>
          </Group>
        </Modal>
      </Stack>
    </PageContainer>
  )
}
